import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { BillingFeature, DeliveryType, TimeUnit } from 'webapp/enums/enums';
import {
	NotificationFilterFrequency,
	NotificationFilterType,
	NotificationScheduleType,
} from 'webapp/enums/notifications';
import { capitalize } from 'webapp/utils/common';

import type { MetricAnomaly } from 'webapp/models-custom';
import DateRange from 'webapp/models-custom/date-range';
import NotificationBase from 'webapp/models-custom/notification/base';

export default class NotificationMetricAnomaly extends NotificationBase {
	@service('model-manager/metric-anomalies')
	declare mmMetricAnomaliesService: Service.ModelManager.MetricAnomalies;

	type = NotificationFilterType.ALERT;
	scheduleType = NotificationScheduleType.ALERT;
	targetObject: MetricAnomaly;
	typeColor = 'var(--databox-red-500)';
	typeIcon: IconName = 'AlertAutomatic';

	override billingFeature = BillingFeature.SMART_ALERTS;

	constructor(model: MetricAnomaly) {
		super();
		this.targetObject = model;
	}

	get title() {
		return this.targetObject.metricSetup?.selectedMetricTitleWithUnit || '';
	}

	get source() {
		return this.targetObject.metricSetup?.source;
	}

	get subtitleText() {
		return this.source?.title || '';
	}

	override get subtitleImage() {
		return this.source?.realAvatar || '';
	}

	get isMine() {
		return true;
	}

	get frequency() {
		return [NotificationFilterFrequency.ANOMALY];
	}

	get frequencyTooltipTextTop() {
		const settingsGranulations = this.targetObject.settings.granulations;
		const granulations: TimeUnit[] = [];
		if (settingsGranulations.daily) {
			granulations.push(TimeUnit.DAY);
		}
		if (settingsGranulations.weekly) {
			granulations.push(TimeUnit.WEEK);
		}
		if (settingsGranulations.monthly) {
			granulations.push(TimeUnit.MONTH);
		}
		const granulationsTexts = granulations.map((timeUnit) => {
			return DateRange.getTimeUnitText(timeUnit, true);
		});
		return `${capitalize(granulationsTexts.join(', '))} for anomalies with a score higher than ${this.targetObject.settings.anomalyScore}`;
	}

	get createdBy() {
		return this.currentUserService.model?.model || null;
	}

	get createdOn() {
		return null;
	}

	get deliveryTypes() {
		const deliveryTypes: DeliveryType[] = [];
		const notifications = this.targetObject.settings.notifications;
		if (notifications.email) {
			deliveryTypes.push(DeliveryType.EMAIL);
		}
		if (notifications.slack.length) {
			deliveryTypes.push(DeliveryType.SLACK);
		}
		return deliveryTypes;
	}

	get emailRecipients() {
		const myId = this.currentUserService.numericId!;
		return this.deliveryTypes.includes(DeliveryType.EMAIL) ? [myId] : [];
	}

	get allRecipients() {
		return this.targetObject.mappedRecipients;
	}

	delete() {
		// is me-only
		const metricAnomaly = this.targetObject;
		void metricAnomaly.deleteAndCatch();
	}
}
